import React, { useContext, useEffect, useState } from 'react'

import withView from 'core/components/withView'
import GlobalContext from 'core/contexts/global-context'
import useFetch from 'core/hooks/use-fetch'
import usePersistedState from 'core/hooks/use-persisted-state'

import Window from 'core/components/Window'
import PostGallery from 'core/components/PostGallery'
import PostList from 'core/components/PostList'
import PostTable from 'core/components/PostTable'

const ProjectIndex = props => {
  const global = useContext(GlobalContext)

  const altLayout = 'list'
  const defaultLayout = 'gallery'
  const [layout, setLayout] = usePersistedState('layout', 'gallery')

  const altOrder = 'asc'
  const defaultOrder = 'des'
  const [sort, setSort] = useState({ data: null })

  const keys = {
    name: ['post_title'],
    type: ['terms', 'project_type', 0, 'name'],
    location: ['terms', 'project_location', 0, 'name'],
    year: ['post_date']
  }

  const getSearchParams = () => {
    const searchParams = new URLSearchParams(props.location.search)
    return {
      sort: searchParams.get('sort') || '',
      order: searchParams.get('order') || defaultOrder,
      filter: searchParams.get('filter') || '',
      value: searchParams.get('value') || ''
    }
  }

  const setSearchParams = params => {
    const searchParams = new URLSearchParams()
    for (let [key, value] of Object.entries(params)) {
      value.length > 0 && searchParams.set(key, value)
    }
    props.history.push(`?${searchParams.toString()}`)
  }

  const sortData = (params, data) => {
    if (!keys[params.sort]) return setSort({ data })

    const reorder = [...data].sort((a, b) => {
      for (let i = 0; i < keys[params.sort].length; i++) {
        a = a[keys[params.sort][i]] ? a[keys[params.sort][i]] : ' '
        b = b[keys[params.sort][i]] ? b[keys[params.sort][i]] : ' '
      }
      return params.order === defaultOrder ? b.localeCompare(a) : a.localeCompare(b)
    })
    setSort({ data: reorder })
  }

  const filterData = (params, data) => {
    const filtered = [...data].filter(f => {
      for (let i = 0; i < keys[params.filter].length; i++) {
        f = f[keys[params.filter][i]] ? f[keys[params.filter][i]] : ' '
        f = keys[params.filter][i] === 'post_date' ? `${f.slice(0, 4)}` : f
      }
      return params.value === f.toLowerCase()
    })
    return filtered
  }

  const { loading, error, data } = useFetch('/projects', {
    onSuccess: data => {
      const params = getSearchParams()
      // props.location.search.length === 0 && setSearchParams(params)

      sortData(params, params.filter.length > 0 ? filterData(params, data) : data)
    }
  })

  useEffect(() => {
    global.setBackground(null)
  }, [])

  const handleClose = () => {
    props.history.replace('/')
  }

  const toggleLayout = () => {
    const toggle = layout === defaultLayout ? altLayout : defaultLayout
    setLayout(toggle)
  }

  const toggleSort = e => {
    const params = getSearchParams()
    const reorder =
      e.target.classList.contains('is-active') && params.order === defaultOrder
        ? altOrder
        : defaultOrder
    const newParams = { ...params, sort: e.target.id, order: reorder }
    setSearchParams(newParams)
    sortData(newParams, sort.data)
  }

  const toggleFilter = e => {
    e.stopPropagation()
    const params = getSearchParams()
    const isActive = e.target.classList.contains('is-active')
    const newParams = isActive
      ? { ...params, filter: '', value: '' }
      : { ...params, filter: e.target.dataset.id, value: e.target.textContent.toLowerCase() }
    setSearchParams(newParams)
    const newData = isActive ? data : filterData(newParams, sort.data)
    sortData(newParams, newData)
    isActive && e.target.classList.remove('is-active')
  }

  if (loading) return <p>Loading...</p>
  if (error) return <p>An error happened: {error.message}</p>

  return (
    <div className={props.className}>
      <Window
        open
        loading={loading}
        mobile={global.mobile}
        fullscreen
        titlebar={{
          title: props.title,
          sort: true,
          layout: layout,
          toggleLayout: toggleLayout,
          close: handleClose
        }}
      >
        {sort.data && sort.data.length > 0 ? (
          layout === 'gallery' ? (
            <PostGallery items={sort.data} />
          ) : global.mobile ? (
            <PostList items={sort.data} toggleFilter={toggleFilter} toggleSort={toggleSort} />
          ) : (
            <PostTable items={sort.data} toggleFilter={toggleFilter} toggleSort={toggleSort} />
          )
        ) : (
          <p>No project items yet.</p>
        )}
      </Window>
    </div>
  )
}

export default withView(ProjectIndex)
